import type { ReactNode } from 'react';
import { DocId } from './doc-id';
import { useTip } from './info-tip';

export type CardTone = 'ok' | 'warn' | 'error' | 'info';

/** A button at the end of the row. Every action is the plain button, except a `danger` one (a destructive action, red text, QA-108). */
export interface CardAction {
  label: string;
  onClick: () => void;
  variant?: 'danger';
  /** Set on a button that opens or closes an area under the row. */
  expanded?: boolean;
  /** QA-133: draw this icon in place of the label. The label stays as the accessible name. */
  icon?: 'trash';
  /** QA-133: a short text shown on hover and on keyboard focus (`aria-describedby`). */
  tooltip?: string;
  /** The action is off. This text is its `title`, so a disabled button still says why on hover. */
  disabledNote?: string;
}

/** QA-83: a row has no marker shape. A row that needs a look tints its status text (QA-79). */
const TONE_CLASS: Record<CardTone, string> = {
  ok: '',
  warn: ' doc-card--warn',
  error: ' doc-card--crit',
  info: '',
};

/**
 * QA-73: the one dense register row for a document, on **Test extraction** and on **Documents**.
 * Line 1 is the document ID (the one link to the document page), the status, and `meta` (the time;
 * the ID already carries the type). QA-79: a warn or crit row tints its status, not the row. `details` are the muted lines under it. The actions sit at the end of the row.
 * `children` is an optional area under the row, for example the result of a test run.
 */
export function DocumentCard({
  tone,
  docId,
  status,
  meta,
  details = [],
  actions,
  wide = false,
  children,
}: {
  tone: CardTone;
  docId: string;
  status: string;
  meta: ReactNode;
  details?: ReactNode[];
  actions: CardAction[];
  /** Reserve room for three buttons, so rows with two or three buttons end their meta at one x. */
  wide?: boolean;
  children?: ReactNode;
}) {
  return (
    <li className={`doc-card${TONE_CLASS[tone]}${wide ? ' doc-card--wide' : ''}`}>
      <div className="doc-card__body">
        <p className="doc-card__line">
          <span className="doc-card__head">
            <a className="doc-card__id" href={`#doc/${docId}`} aria-label={docId} title={docId}>
              <DocId id={docId} />
            </a>{' '}
            <span className="doc-card__status" data-tone={tone}>
              {status}
            </span>
          </span>
          <span className="doc-card__meta">{meta}</span>
        </p>
        {details.map((detail, index) => (
          <p key={index} className="doc-card__detail">
            {detail}
          </p>
        ))}
      </div>
      {/* Rendered with no action too, so the meta of every row ends at the same place. */}
      <div className="doc-card__actions">
        {actions.map((action) => (
          <CardButton key={action.label} action={action} />
        ))}
      </div>
      {children && <div className="doc-card__extra">{children}</div>}
    </li>
  );
}

/** The trash icon is drawn inline like the other icons (no icon library); the button names it. */
function TrashIcon() {
  return (
    <svg className="btn__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm-3 6h12l-1 12H7L6 9Zm4 2v8h1.5v-8H10Zm2.5 0v8H14v-8h-1.5Z" />
    </svg>
  );
}

function CardButton({ action }: { action: CardAction }) {
  // An action button never pins its tip: a click or tap runs the action at once.
  const tip = useTip(action.tooltip ?? '', false);
  const danger = action.variant === 'danger';
  const className = `${danger ? 'btn btn--text btn--danger-text' : 'btn'}${action.icon ? ' btn--icon' : ''}`;
  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={action.icon ? action.label : undefined}
        aria-expanded={action.expanded}
        {...(action.tooltip && !action.disabledNote ? tip.buttonProps : {})}
        disabled={action.disabledNote !== undefined}
        title={action.disabledNote}
        onClick={() => {
          tip.hide();
          action.onClick();
        }}
      >
        {action.icon === 'trash' ? <TrashIcon /> : action.label}
      </button>
      {action.tooltip && !action.disabledNote && tip.bubble}
    </>
  );
}
